import { useEffect, useState } from 'react';
import { useUnit } from 'effector-react';
import { useQuery } from '@tanstack/react-query';
import styled, { keyframes } from 'styled-components';
import {
  Container,
  Em,
  Button,
  Arrow,
  Field,
  Label,
  Input,
  Textarea,
  FieldHint,
  Chip,
  ChipStack,
  ColHead,
  Badge,
} from '@/components/primitives';
import {
  $form,
  $formHydrated,
  $activeHandles,
  TOTAL_STEPS,
  fieldChanged,
  topicToggled,
  toneSelected,
  handleChanged,
  formHydrated,
  nicknameAdded,
  nicknameRemoved,
  type OnboardForm,
} from '@/model/onboard';
import { $verified } from '@/model/kyc';
import { confirmSearchOpened } from '@/model/modals';
import { navigated } from '@/model/router';
import { startSearchFx } from '@/model/search';
import { queryFn } from '@/api';
import type { OnboardDictionary, OnboardDraft } from '@/api/mocks/lume';

const fade = keyframes`from { opacity: 0; transform: translateY(8px); } to { opacity: 1; transform: translateY(0); }`;

const View = styled.section`
  padding: ${({ theme }) => `${theme.spacing[11]} 0 ${theme.spacing[14]}`};
  animation: ${fade} ${({ theme }) => theme.motion.duration.panel} ${({ theme }) => theme.motion.easing.out};
`;

const Head = styled.div`
  padding-bottom: ${({ theme }) => theme.spacing[8]};
  margin-bottom: ${({ theme }) => theme.spacing[9]};
  border-bottom: 1px solid ${({ theme }) => theme.line.soft};
`;

const StepBar = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing[4]};
  font-family: ${({ theme }) => theme.typography.fontFamilies.mono};
  font-size: ${({ theme }) => theme.typography.fontSizes.monoMd};
  letter-spacing: ${({ theme }) => theme.typography.letterSpacings.wider};
  text-transform: uppercase;
  color: ${({ theme }) => theme.ink[3]};
  margin-bottom: ${({ theme }) => theme.spacing[8]};
`;

const Bar = styled.div`
  flex: 1;
  max-width: 280px;
  height: 2px;
  background: ${({ theme }) => theme.line.line};
  border-radius: 2px;
  overflow: hidden;
`;

const BarFill = styled.span<{ $percent: number }>`
  display: block;
  height: 100%;
  width: ${({ $percent }) => $percent}%;
  background: ${({ theme }) => theme.tone.halo.base};
  transition: width ${({ theme }) => theme.motion.duration.slow} ${({ theme }) => theme.motion.easing.out};
`;

const HeadTitle = styled.h2`
  font-family: ${({ theme }) => theme.typography.fontFamilies.serif};
  font-weight: ${({ theme }) => theme.typography.fontWeights.regular};
  font-size: ${({ theme }) => theme.typography.fontSizes.hLg};
  line-height: ${({ theme }) => theme.typography.lineHeights.snug};
  letter-spacing: ${({ theme }) => theme.typography.letterSpacings.tight};
  font-variation-settings: 'opsz' 60;
  margin-bottom: ${({ theme }) => theme.spacing[3]};

  em {
    font-family: ${({ theme }) => theme.typography.fontFamilies.serifEd};
    font-style: italic;
    color: ${({ theme }) => theme.tone.halo.base};
    font-variation-settings: normal;
  }

  @media (max-width: 600px) {
    font-size: ${({ theme }) => theme.typography.fontSizes.hMd};
  }
`;

const HeadLede = styled.p`
  font-size: ${({ theme }) => theme.typography.fontSizes.bodyLg};
  color: ${({ theme }) => theme.ink[2]};
  max-width: ${({ theme }) => theme.widths.reading};
  line-height: ${({ theme }) => theme.typography.lineHeights.relaxed};
`;

const StepGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: ${({ theme }) => theme.spacing[10]};

  @media (max-width: 760px) {
    grid-template-columns: 1fr;
    gap: ${({ theme }) => theme.spacing[8]};
  }
`;

const HandleRow = styled(Field)`
  display: grid;
  grid-template-columns: 120px 20px 1fr;
  gap: ${({ theme }) => theme.spacing[3]};
  align-items: center;
  margin-bottom: ${({ theme }) => theme.spacing[3]};

  ${Label} {
    grid-column: 1;
    margin-bottom: 0;
  }

  @media (max-width: 540px) {
    grid-template-columns: 1fr;
    gap: ${({ theme }) => theme.spacing[2]};

    ${Label} {
      grid-column: auto;
    }
  }
`;

const Prefix = styled.span`
  grid-column: 2;
  text-align: center;
  color: ${({ theme }) => theme.ink[4]};
  font-family: ${({ theme }) => theme.typography.fontFamilies.mono};
  font-size: ${({ theme }) => theme.typography.fontSizes.body};

  @media (max-width: 540px) {
    display: none;
  }
`;

const SourceSummary = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${({ theme }) => theme.spacing[2]};
  padding: ${({ theme }) => theme.spacing[5]};
  background: ${({ theme }) => theme.surface.deep};
  border: 1px solid ${({ theme }) => theme.line.soft};
  border-radius: ${({ theme }) => theme.radii.sm};
  margin-top: ${({ theme }) => theme.spacing[5]};
  min-height: 60px;
  align-items: center;
`;

const Muted = styled.span`
  color: ${({ theme }) => theme.ink[4]};
  font-family: ${({ theme }) => theme.typography.fontFamilies.mono};
  font-size: ${({ theme }) => theme.typography.fontSizes.monoMd};
`;

const Foot = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-top: ${({ theme }) => theme.spacing[8]};
  margin-top: ${({ theme }) => theme.spacing[9]};
  border-top: 1px solid ${({ theme }) => theme.line.soft};
  flex-wrap: wrap;
  gap: ${({ theme }) => theme.spacing[3]};
`;

const Info = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing[3]};
  font-family: ${({ theme }) => theme.typography.fontFamilies.sans};
  font-size: ${({ theme }) => theme.typography.fontSizes.uiSm};
  color: ${({ theme }) => theme.ink[3]};
`;

const InfoDot = styled.span`
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: ${({ theme }) => theme.tone.aurora.base};
  box-shadow: 0 0 0 4px ${({ theme }) => theme.fx.ringAurora};
`;

const Skeleton = styled.div`
  padding: ${({ theme }) => theme.spacing[10]} 0;
  text-align: center;
  font-family: ${({ theme }) => theme.typography.fontFamilies.mono};
  font-size: ${({ theme }) => theme.typography.fontSizes.monoMd};
  letter-spacing: ${({ theme }) => theme.typography.letterSpacings.wider};
  text-transform: uppercase;
  color: ${({ theme }) => theme.ink[4]};
`;

type StepMeta = { title: React.ReactNode; lede: string };

const STEP_META: StepMeta[] = [
  {
    title: (
      <>
        Знакомство. <Em>Коротко.</Em>
      </>
    ),
    lede: 'Lume не задаёт сто вопросов. Нам нужен костяк: кто вы, чем занимаетесь и что для вас важно. Остальное вы расскажете голосом — через источники.',
  },
  {
    title: (
      <>
        Темы и <Em>тон</Em>.
      </>
    ),
    lede: 'Что для вас важно и как звучит ваш голос — эти две точки задают угол, под которым Lume смотрит на ваш цифровой след.',
  },
  {
    title: (
      <>
        Источники <Em>голоса</Em>.
      </>
    ),
    lede: 'Укажите только то, что вы согласны показать. Lume читает публичный контент — без паролей и приватных сообщений.',
  },
];

const HANDLE_FIELDS: {
  key: keyof OnboardForm['handles'];
  label: string;
  prefix: string;
  placeholder: string;
}[] = [
  { key: 'linkedin', label: 'LinkedIn', prefix: '@', placeholder: 'профиль или URL' },
  { key: 'github', label: 'GitHub', prefix: '@', placeholder: 'username' },
  { key: 'telegram', label: 'Telegram', prefix: '@', placeholder: 'канал или username' },
  { key: 'site', label: 'Website', prefix: '↗', placeholder: 'mp.design' },
  { key: 'x', label: 'X / Twitter', prefix: '@', placeholder: 'username' },
];

const TONE_BADGES = ['aurora', 'solar', 'mist', 'halo'] as const;

// === Валидация полей step-1 ===========================================
// Простые user-input проверки. Backend zod дублирует эти же правила —
// frontend гейты лишь дают inline-фидбек и блокируют «Далее».

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
// E.164: + и 8–15 цифр. Без пробелов / скобок — оставляем «жёсткий» формат,
// пользователь должен убрать +7 (495) ... → +7495...
const PHONE_RE = /^\+[1-9]\d{7,14}$/;
// Generic handle: то, что покрывает github/telegram/instagram/etc. без
// привязки к конкретной платформе. 1–40 символов, буквы/цифры/._- .
const HANDLE_RE = /^[A-Za-z0-9._-]{1,40}$/;

// Минимум 13 лет (защита от бессмысленных данных), максимум 120 — отсекает
// явно ошибочные годы рождения (1850 и т.п.). На pipeline это не влияет:
// просто sanity-bound на ввод.
const MIN_AGE_YEARS = 13;
const MAX_AGE_YEARS = 120;

const validateBirthDate = (raw: string): string | null => {
  if (!raw) return 'Дата рождения обязательна';
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(raw);
  if (!m) return 'Формат: ДД.ММ.ГГГГ';
  const d = new Date(`${raw}T00:00:00Z`);
  if (Number.isNaN(d.getTime())) return 'Невалидная дата';
  const now = new Date();
  if (d.getTime() > now.getTime()) return 'Дата в будущем';
  const age = (now.getTime() - d.getTime()) / (1000 * 60 * 60 * 24 * 365.25);
  if (age < MIN_AGE_YEARS) return `Слишком юный возраст (< ${MIN_AGE_YEARS} лет)`;
  if (age > MAX_AGE_YEARS) return `Слишком давно (> ${MAX_AGE_YEARS} лет)`;
  return null;
};

const validateName = (raw: string, label: string): string | null => {
  const v = raw.trim();
  if (!v) return `${label} обязательно`;
  if (v.length < 2) return `${label} слишком короткое`;
  if (v.length > 100) return `${label} слишком длинное`;
  return null;
};

const validateOptionalEmail = (raw: string): string | null => {
  const v = raw.trim();
  if (!v) return null; // optional
  // Несколько email через запятую — каждый валидируем.
  const parts = v
    .split(/[,;]/)
    .map((p) => p.trim())
    .filter(Boolean);
  for (const p of parts) {
    if (!EMAIL_RE.test(p)) return `Невалидный email: ${p}`;
  }
  return null;
};

const validateOptionalPhone = (raw: string): string | null => {
  const v = raw.trim();
  if (!v) return null;
  const parts = v
    .split(/[,;]/)
    .map((p) => p.trim())
    .filter(Boolean);
  for (const p of parts) {
    if (!PHONE_RE.test(p)) return `Телефон в формате +7..., без скобок и пробелов: ${p}`;
  }
  return null;
};

const validateNickname = (raw: string): string | null => {
  const v = raw.trim().replace(/^@/, '');
  if (!v) return null;
  if (!HANDLE_RE.test(v)) return 'Только латиница / цифры / . _ -';
  return null;
};

const NicknameWrap = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${({ theme }) => theme.spacing[2]};
  align-items: center;
  padding: ${({ theme }) => theme.spacing[2]};
  border: 1px solid ${({ theme }) => theme.line.line};
  border-radius: ${({ theme }) => theme.radii.sm};
  background: ${({ theme }) => theme.surface.bg};
`;

const NicknamePill = styled.span`
  display: inline-flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing[2]};
  padding: ${({ theme }) => `${theme.spacing[1]} ${theme.spacing[3]}`};
  background: ${({ theme }) => theme.surface.elev};
  border: 1px solid ${({ theme }) => theme.line.soft};
  border-radius: ${({ theme }) => theme.radii.pill};
  font-family: ${({ theme }) => theme.typography.fontFamilies.mono};
  font-size: ${({ theme }) => theme.typography.fontSizes.monoSm};
  color: ${({ theme }) => theme.ink[1]};

  button {
    background: none;
    border: none;
    cursor: pointer;
    color: ${({ theme }) => theme.ink[3]};
    font-size: 16px;
    line-height: 1;
    padding: 0;
    &:hover {
      color: ${({ theme }) => theme.ink[1]};
    }
  }
`;

const NicknameInput = styled.input`
  flex: 1 1 120px;
  min-width: 120px;
  background: transparent;
  border: none;
  font-family: ${({ theme }) => theme.typography.fontFamilies.mono};
  font-size: ${({ theme }) => theme.typography.fontSizes.monoMd};
  color: ${({ theme }) => theme.ink[1]};
  padding: ${({ theme }) => `${theme.spacing[1]} ${theme.spacing[2]}`};
  &:focus {
    outline: none;
  }
  &::placeholder {
    color: ${({ theme }) => theme.ink[4]};
  }
`;

type NicknameTagInputProps = {
  values: string[];
  onAdd: (v: string) => void;
  onRemove: (idx: number) => void;
};

// Tag-pill ввод никнеймов. Enter / запятая — добавить, Backspace на пустом —
// удалить последний. Ограничение: макс 20 ников (защита от спама + zod-cap
// на бэке такой же). Невалидный ввод — короткий toast/inline (HANDLE_RE не
// прошёл). Дубликаты отсекаются на уровне reducer'а в model/onboard.ts.
const MAX_NICKNAMES = 20;

const NicknameTagInput = ({ values, onAdd, onRemove }: NicknameTagInputProps) => {
  const [draft, setDraft] = useState('');
  const [warning, setWarning] = useState<string | null>(null);

  const commit = () => {
    const v = draft.trim().replace(/^@/, '');
    if (!v) return;
    const err = validateNickname(v);
    if (err) {
      setWarning(err);
      return;
    }
    if (values.length >= MAX_NICKNAMES) {
      setWarning(`Максимум ${MAX_NICKNAMES} ников`);
      return;
    }
    onAdd(v);
    setDraft('');
    setWarning(null);
  };

  return (
    <>
      <NicknameWrap>
        {values.map((n, i) => (
          <NicknamePill key={`${n}-${i}`}>
            @{n}
            <button type="button" aria-label={`Удалить ${n}`} onClick={() => onRemove(i)}>
              ×
            </button>
          </NicknamePill>
        ))}
        <NicknameInput
          type="text"
          placeholder={values.length === 0 ? 'ivan_dev' : 'добавить ник…'}
          value={draft}
          maxLength={40}
          onChange={(e) => {
            setDraft(e.target.value);
            if (warning) setWarning(null);
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ',') {
              e.preventDefault();
              commit();
            } else if (e.key === 'Backspace' && draft === '' && values.length > 0) {
              onRemove(values.length - 1);
            }
          }}
          onBlur={commit}
        />
      </NicknameWrap>
      {warning && <FieldHint $error>{warning}</FieldHint>}
    </>
  );
};

export const Onboard = () => {
  const [step, setStep] = useState(1);
  const [form, hydrated, activeHandles, verified] = useUnit([
    $form,
    $formHydrated,
    $activeHandles,
    $verified,
  ]);

  const draftQuery = useQuery({
    queryKey: ['/api/lume/onboard/draft'],
    queryFn: queryFn<OnboardDraft>('/api/lume/onboard/draft'),
    staleTime: Infinity,
  });

  const dictionaryQuery = useQuery({
    queryKey: ['/api/lume/onboard/dictionary'],
    queryFn: queryFn<OnboardDictionary>('/api/lume/onboard/dictionary'),
    staleTime: Infinity,
  });

  useEffect(() => {
    if (!hydrated && draftQuery.data) {
      formHydrated(draftQuery.data);
    }
  }, [draftQuery.data, hydrated]);

  const percent = Math.round((step / TOTAL_STEPS) * 100);
  const meta = STEP_META[step - 1]!;
  const topics = dictionaryQuery.data?.topics ?? [];
  const tones = dictionaryQuery.data?.tones ?? [];

  const submitting = useUnit(startSearchFx.pending);

  // Inline-ошибки: показываем рядом с полем когда blur (или просто если пусто/мусор).
  // canAdvance = все required-проверки прошли + опциональные не invalid.
  const errors = {
    last_name: validateName(form.last_name, 'Фамилия'),
    first_name: validateName(form.first_name, 'Имя'),
    birth_date: validateBirthDate(form.birth_date),
    email: validateOptionalEmail(form.email),
    phone: validateOptionalPhone(form.phone),
  };

  const canAdvance = (() => {
    if (step !== 1) return true;
    return (
      errors.last_name === null &&
      errors.first_name === null &&
      errors.birth_date === null &&
      errors.email === null &&
      errors.phone === null
    );
  })();

  // Сводка введённых данных для confirm-модалки. Показываем только то,
  // что реально влияет на pipeline или может содержать опечатку
  // (identifier'ы). About / topics / tone / role — для narrative,
  // не для дизамбигуации однофамильцев — пропускаем, чтобы модалка
  // не превратилась в простыню.
  const buildConfirmRows = (): { label: string; value: string }[] => {
    const fullName = [form.last_name, form.first_name, form.patronymic]
      .map((s) => s.trim())
      .filter(Boolean)
      .join(' ');
    return [
      { label: 'ФИО', value: fullName },
      { label: 'Дата рождения', value: form.birth_date },
      { label: 'Email', value: form.email },
      { label: 'Телефон', value: form.phone },
      {
        label: 'Никнеймы',
        value: form.nicknames.length ? form.nicknames.map((n) => `@${n}`).join(', ') : '',
      },
      { label: 'LinkedIn', value: form.handles.linkedin },
      { label: 'GitHub', value: form.handles.github },
      { label: 'Telegram', value: form.handles.telegram },
      { label: 'Сайт', value: form.handles.site },
      { label: 'X / Twitter', value: form.handles.x },
      { label: 'Город', value: form.city },
    ];
  };

  const onNext = () => {
    if (submitting) return;
    if (!canAdvance) return;
    if (step < TOTAL_STEPS) {
      setStep((s) => s + 1);
      return;
    }
    if (!verified) {
      navigated('kyc');
      return;
    }
    confirmSearchOpened({
      rows: buildConfirmRows(),
      onConfirm: () => {
        void startSearchFx().then(
          () => navigated('processing'),
          () => navigated('processing'),
        );
      },
    });
  };
  const onBack = () => setStep((s) => Math.max(1, s - 1));

  return (
    <View>
      <Container $narrow>
        <Head>
          <StepBar>
            Шаг {step} из {TOTAL_STEPS}
            <Bar>
              <BarFill $percent={percent} />
            </Bar>
          </StepBar>
          <HeadTitle>{meta.title}</HeadTitle>
          <HeadLede>{meta.lede}</HeadLede>
        </Head>

        {!hydrated ? (
          <Skeleton>загружаем черновик…</Skeleton>
        ) : step === 1 ? (
          <StepGrid>
            <div>
              <ColHead>о вас</ColHead>
              <Field>
                <Label>Фамилия</Label>
                <Input
                  type="text"
                  placeholder="Иванов"
                  maxLength={100}
                  value={form.last_name}
                  onChange={(e) => fieldChanged({ key: 'last_name', value: e.target.value })}
                  aria-invalid={errors.last_name !== null}
                />
                {errors.last_name && <FieldHint $error>{errors.last_name}</FieldHint>}
              </Field>
              <Field>
                <Label>Имя</Label>
                <Input
                  type="text"
                  placeholder="Иван"
                  maxLength={100}
                  value={form.first_name}
                  onChange={(e) => fieldChanged({ key: 'first_name', value: e.target.value })}
                  aria-invalid={errors.first_name !== null}
                />
                {errors.first_name && <FieldHint $error>{errors.first_name}</FieldHint>}
              </Field>
              <Field>
                <Label>Отчество (опционально)</Label>
                <Input
                  type="text"
                  placeholder="Иванович"
                  maxLength={100}
                  value={form.patronymic}
                  onChange={(e) => fieldChanged({ key: 'patronymic', value: e.target.value })}
                />
              </Field>
              <Field>
                <Label>Дата рождения</Label>
                <Input
                  type="date"
                  max={new Date().toISOString().slice(0, 10)}
                  value={form.birth_date}
                  onChange={(e) => fieldChanged({ key: 'birth_date', value: e.target.value })}
                  required
                  aria-invalid={errors.birth_date !== null}
                />
                {errors.birth_date ? (
                  <FieldHint $error>{errors.birth_date}</FieldHint>
                ) : (
                  <FieldHint>
                    Полная дата обязательна — без неё нельзя отличить однофамильцев. Формат: ДД.ММ.ГГГГ.
                  </FieldHint>
                )}
              </Field>
              <Field>
                <Label>Email (опционально)</Label>
                <Input
                  type="email"
                  inputMode="email"
                  autoCapitalize="none"
                  spellCheck={false}
                  placeholder="ivan@example.com"
                  maxLength={200}
                  value={form.email}
                  onChange={(e) => fieldChanged({ key: 'email', value: e.target.value })}
                  aria-invalid={errors.email !== null}
                />
                {errors.email ? (
                  <FieldHint $error>{errors.email}</FieldHint>
                ) : (
                  <FieldHint>
                    Любой identifier резко улучшает поиск. Несколько значений — через запятую.
                  </FieldHint>
                )}
              </Field>
              <Field>
                <Label>Телефон (опционально)</Label>
                <Input
                  type="tel"
                  inputMode="tel"
                  placeholder="+79991234567"
                  maxLength={50}
                  value={form.phone}
                  onChange={(e) => fieldChanged({ key: 'phone', value: e.target.value })}
                  aria-invalid={errors.phone !== null}
                />
                {errors.phone ? (
                  <FieldHint $error>{errors.phone}</FieldHint>
                ) : (
                  <FieldHint>Формат E.164: +79991234567 (без скобок и пробелов).</FieldHint>
                )}
              </Field>
              <Field>
                <Label>Никнеймы (опционально)</Label>
                <NicknameTagInput
                  values={form.nicknames}
                  onAdd={(v) => nicknameAdded(v)}
                  onRemove={(idx) => nicknameRemoved(idx)}
                />
                <FieldHint>Можно ввести несколько — Enter или запятая добавляет ник в список.</FieldHint>
              </Field>
              <Field>
                <Label>Роль или профессия</Label>
                <Input
                  type="text"
                  maxLength={200}
                  value={form.role}
                  onChange={(e) => fieldChanged({ key: 'role', value: e.target.value })}
                />
              </Field>
              <Field>
                <Label>Город · часовой пояс</Label>
                <Input
                  type="text"
                  maxLength={100}
                  value={form.city}
                  onChange={(e) => fieldChanged({ key: 'city', value: e.target.value })}
                />
              </Field>
            </div>
            <div>
              <ColHead>два слова о себе</ColHead>
              <Field>
                <Label>Чем вы занимаетесь на самом деле</Label>
                <Textarea
                  maxLength={4000}
                  value={form.about}
                  onChange={(e) => fieldChanged({ key: 'about', value: e.target.value })}
                />
                <FieldHint>Несколько предложений — этого достаточно. Lume не любит анкеты.</FieldHint>
              </Field>
            </div>
          </StepGrid>
        ) : step === 2 ? (
          <div>
            <ColHead>темы, которые звучат у вас</ColHead>
            <FieldHint style={{ marginBottom: '20px', marginTop: 0 }}>
              Отметьте 3–6 тем. По ним Lume скорректирует тон отчёта.
            </FieldHint>
            <ChipStack>
              {topics.map((t) => (
                <Chip key={t} $on={form.topics.includes(t)} onClick={() => topicToggled(t)}>
                  {t}
                </Chip>
              ))}
            </ChipStack>

            <ColHead style={{ marginTop: '40px' }}>ваш голос — какой он</ColHead>
            <FieldHint style={{ marginBottom: '20px', marginTop: 0 }}>
              Выберите утверждение, которое ближе всего.
            </FieldHint>
            <ChipStack>
              {tones.map((t) => (
                <Chip key={t} $halo $on={form.tone === t} onClick={() => toneSelected(t)}>
                  {t}
                </Chip>
              ))}
            </ChipStack>
          </div>
        ) : (
          <StepGrid>
            <div>
              <ColHead>источники голоса</ColHead>
              {HANDLE_FIELDS.map((h) => (
                <HandleRow key={h.key}>
                  <Label>{h.label}</Label>
                  <Prefix>{h.prefix}</Prefix>
                  <Input
                    type="text"
                    placeholder={h.placeholder}
                    maxLength={300}
                    value={form.handles[h.key]}
                    onChange={(e) => handleChanged({ key: h.key, value: e.target.value })}
                  />
                </HandleRow>
              ))}
            </div>
            <div>
              <ColHead>подтверждение</ColHead>
              <FieldHint style={{ marginBottom: '16px', marginTop: 0 }}>
                Эти источники попадут в отчёт. Мы не запрашиваем пароли — только публичный контент.
              </FieldHint>
              <SourceSummary>
                {activeHandles.length === 0 ? (
                  <Muted>Источники появятся здесь по мере ввода…</Muted>
                ) : (
                  activeHandles.map((a, i) => (
                    <Badge key={a.name} $tone={TONE_BADGES[i % TONE_BADGES.length]}>
                      {a.name} · {a.value}
                    </Badge>
                  ))
                )}
              </SourceSummary>
              <FieldHint style={{ marginTop: '32px' }}>
                Ваши данные шифруются и не передаются третьим сторонам. Отчёт можно удалить в любой момент.
              </FieldHint>
            </div>
          </StepGrid>
        )}

        <Foot>
          <Info>
            <InfoDot />
            <span>Черновик сохраняется автоматически</span>
          </Info>
          <div style={{ display: 'flex', gap: '12px' }}>
            <Button $variant="ghost" onClick={onBack} disabled={step === 1}>
              Назад
            </Button>
            <Button onClick={onNext} disabled={submitting || !canAdvance}>
              {step === TOTAL_STEPS ? (submitting ? 'Запускаем…' : 'Собрать отчёт') : 'Далее'}{' '}
              <Arrow>→</Arrow>
            </Button>
          </div>
        </Foot>
      </Container>
    </View>
  );
};
